import { orgSelectors } from 'ducks/orgs'
import { SellerIntegrationType } from 'opensolar-common'
import { RootState } from 'types/state'
import { manualCostIntegration } from './useEnabledHardwareSuppliers'

export type SupplierConnectionChecker = (filterKey: string) => boolean

export type GetPreferenceSupplierConfigOptions = {
  quoteReviewOnly?: boolean
}

export function getSupplierConfigsForPreferenceSort(state: RootState): SellerIntegrationType[] {
  return [...orgSelectors.getEnabledSellerIntegrationsConfig(state), manualCostIntegration]
}

export function getSupplierIsConnectedFromState(state: RootState, filterKey: string): boolean {
  const config = getSupplierConfigsForPreferenceSort(state).find((item) => item.filterKey === filterKey)
  return config?.isConnected ?? false
}

export function getPreferenceSupplierConfigFromState(
  state: RootState,
  getIsConnected: SupplierConnectionChecker,
  options?: GetPreferenceSupplierConfigOptions
): SellerIntegrationType[] {
  const supplierConfigs = getSupplierConfigsForPreferenceSort(state)
  const getSupplierPreferenceOrder = (filterKey: string) => orgSelectors.getSupplierPreferenceOrder(state, filterKey)
  const quoteReviewOnly = options?.quoteReviewOnly ?? false
  const eligibleConfigs = quoteReviewOnly
    ? supplierConfigs.filter((config) => config?.isQuoteReviewEnabled)
    : supplierConfigs

  const { withPreference, withoutPreference } = eligibleConfigs.reduce(
    (acc, config) => {
      if (getSupplierPreferenceOrder(config.filterKey) !== null) {
        acc.withPreference.push(config)
      } else if (config.filterKey !== manualCostIntegration.filterKey) {
        acc.withoutPreference.push(config)
      }

      return acc
    },
    {
      withPreference: [] as SellerIntegrationType[],
      withoutPreference: [] as SellerIntegrationType[],
    }
  )

  const sortedWithPreference = withPreference.sort((a, b) => {
    return (
      (getSupplierPreferenceOrder(a.filterKey) ?? Number.MAX_SAFE_INTEGER) -
      (getSupplierPreferenceOrder(b.filterKey) ?? Number.MAX_SAFE_INTEGER)
    )
  })

  const sortedWithoutPreference = withoutPreference.sort((a, b) => {
    const aConnected = getIsConnected(a.filterKey)
    const bConnected = getIsConnected(b.filterKey)

    if (aConnected && !bConnected) return -1
    if (!aConnected && bConnected) return 1
    return a.name.localeCompare(b.name)
  })

  const result = [...sortedWithPreference]

  if (!quoteReviewOnly && getSupplierPreferenceOrder(manualCostIntegration.filterKey) === null) {
    result.push(manualCostIntegration)
  }

  result.push(...sortedWithoutPreference)

  return result
}

export function getPrimaryPreferredSupplierFilterKeyFromState(
  state: RootState,
  getIsConnected: SupplierConnectionChecker = (filterKey) => getSupplierIsConnectedFromState(state, filterKey)
): string | null {
  return getPreferenceSupplierConfigFromState(state, getIsConnected)[0]?.filterKey ?? null
}

/** First preferred supplier that can supply catalogue hardware (skips manual / component cost). */
export function getHardwarePreferredSupplierFilterKeyFromState(
  state: RootState,
  getIsConnected: SupplierConnectionChecker = (filterKey) => getSupplierIsConnectedFromState(state, filterKey)
): string | null {
  return (
    getPreferenceSupplierConfigFromState(state, getIsConnected).find(
      (config) => config.filterKey !== manualCostIntegration.filterKey
    )?.filterKey ?? null
  )
}
